import i18n from "i18n";
import { MessageKey, ResourceKey } from "i18n/types/i18n.keys";
import { createElement, Fragment, ReactNode } from "react";
import { useLocaleStore } from "stores/useLocaleStore";

// * Namespace 정의
type Namespace = "message" | "resource";

// * getMessage args 인자 타입
type Args = Array<ResourceKey | (string & {})>;

// * namespace에 따른 key 타입
type TranslationKey<T extends Namespace> = T extends "message"
  ? MessageKey
  : ResourceKey;

/**
 * getResource
 * - 리소스(namespace="resource") 기준으로 번역 문자열 반환
 * - args 배열 처리 방식:
 *   1. {{_1}}, {{_2}} 등 치환
 *   2. ResourceKey를 전달하면 getResource를 통해 자동 번역
 *   3. 일반 문자열을 전달하면 그대로 사용
 *
 * @param key {ResourceKey} - 번역할 리소스 키
 * @param args {Args} [optional] - 치환용 배열 (ResourceKey 또는 문자열)
 * @returns {string} - 치환이 적용된 번역 문자열
 *
 * @example
 * getResource("COMMON.PROJECT_NAME", ["COMMON.COMPANY"]);
 * // "Company Name"
 */
export const getResource = (key: ResourceKey, args?: Args): string => {
  const replace: Record<string, string> = {};

  args?.forEach((value, index) => {
    const resource =
      typeof value === "string" && i18n.exists(value, { ns: "resource" })
        ? getResource(value as ResourceKey) // 재귀 호출로 ResourceKey 자동 번역
        : value;

    replace[`_${index + 1}`] = resource;
  });

  return i18n.t(key, { ns: "resource", replace });
};

/**
 * getMessage
 * - 메시지(namespace="message") 기준으로 번역 문자열 반환
 * - args 배열 처리 방식:
 *   1. {{_1}}, {{_2}} 등 치환
 *   2. ResourceKey를 전달하면 getResource를 통해 자동 번역
 *   3. 일반 문자열을 전달하면 그대로 사용
 *
 * @param key {MessageKey} - 번역할 메시지 키
 * @param args {Args} [optional] - 치환용 배열 (ResourceKey 또는 문자열)
 * @returns {string} - 치환이 적용된 번역 문자열
 *
 * @example
 * getMessage("COMMON.CONFIRM_DELETE", ["COMMON.PROJECTS"]);
 * // "프로젝트을(를) 삭제하시겠습니까?"
 */
export const getMessage = (key: MessageKey, args?: Args): string => {
  const replace: Record<string, string> = {};

  args?.forEach((value, index) => {
    const resource =
      typeof value === "string" && i18n.exists(value, { ns: "resource" })
        ? getResource(value as ResourceKey)
        : value;

    replace[`_${index + 1}`] = resource;
  });

  return i18n.t(key, { ns: "message", replace });
};

/**
 * getTranslation
 * - namespace 기반으로 getMessage / getResource를 자동 호출
 *
 * @param ns {T} - "message" | "resource"
 * @param key {TranslationKey<T>} - 해당 namespace에 맞는 키
 * @param args {TranslationArgs<T>} [optional]
 *   - args 배열 처리 방식:
 *      1. {{_1}}, {{_2}} 등 치환
 *      2. ResourceKey를 전달하면 getResource를 통해 자동 번역
 *      3. 일반 문자열을 전달하면 그대로 사용
 * @returns {string} - 번역된 문자열
 *
 * @example
 * getTranslation("message", "COMMON.CONFIRM_DELETE", ["Test Project"]);
 * // "Test Project을(를) 삭제하시겠습니까?"
 * getTranslation("resource", "COMMON.PROJECTS");
 * // "Projects"
 */

export const getTranslation = <T extends Namespace>(
  ns: T,
  key: TranslationKey<T>,
  args?: Args
): string => {
  const { isResourceTestMode } = useLocaleStore.getState();

  // * 임시 테스트용 코드
  // * 임시 테스트용 코드
  // i18n 존재 여부 확인
  const langExists = i18n.exists(key, { ns });

  // 언어가 없으면 resource 계정일 때만 특수 표시
  if (!langExists && isResourceTestMode) {
    const currentLang = i18n.language || "ko-KR";
    return `${currentLang} 번역 없음 {${key}}`;
  }
  if (isResourceTestMode) return `$$-${key}`;

  return ns === "message"
    ? getMessage(key as MessageKey, args)
    : getResource(key as ResourceKey, args);
};

/**
 * getTranslationNode
 * - 기존 getTranslation 을 확장하여 JSX args 를 지원하는 wrapper
 * - getTranslation 의 타입(ns, key) 시그니처를 그대로 유지
 */
export const getTranslationNode = <T extends Namespace>(
  ns: T,
  key: TranslationKey<T>,
  args?: Array<ReactNode>
): ReactNode => {
  if (!args || args.length === 0) {
    let raw = getTranslation(ns, key);

    raw = raw.replace(/\\n/g, "\n");

    return raw
      .split("\n")
      .map((line, idx) =>
        createElement(
          Fragment,
          { key: idx },
          line,
          idx < raw.split("\n").length - 1 ? createElement("br") : null
        )
      );
  }

  const markers = args.map((_, i) => `__jsx_marker_${i}__`);

  let raw = getTranslation(ns, key, markers);

  raw = raw.replace(/\\n/g, "\n");

  const parts = raw.split(/(__jsx_marker_\d+__)/g);

  return createElement(
    Fragment,
    null,
    parts.map((part, index) => {
      const match = part.match(/__jsx_marker_(\d+)__/);

      if (match) {
        const jsxIndex = Number(match[1]);
        return createElement(Fragment, { key: index }, args[jsxIndex]);
      }

      const lines = part.split("\n");

      return createElement(
        Fragment,
        { key: index },
        ...lines.flatMap((line, lineIdx) => [
          line,
          lineIdx < lines.length - 1
            ? createElement("br", { key: `br-${lineIdx}` })
            : null,
        ])
      );
    })
  );
};
